import { useState } from 'react';
import {
  IconButton,
  Box,
  Stack,
  MenuItem,
  Popover,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Iconify from '@/components/Iconify';
import useLocales from '@/hooks/useLocales';
import useBoletiaConfig from '@/hooks/useBoletiaConfig';
import useEventContext from '@/contexts/event/useEventContext';

const localeEnabled = process.env.NEXT_PUBLIC_LOCALE_ENABLED
  ? process.env.NEXT_PUBLIC_LOCALE_ENABLED === 'true'
  : false;

const ExpandMore = styled((props) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

export default function LanguageSelector() {
  const [open, setOpen] = useState();
  const { currentLang, allLangs, onChangeLang } = useLocales();
  const { isEventsLng } = useBoletiaConfig();
  
  const {
    event: {
      event: { id },
    },
  } = useEventContext();

  const handleOpen = (ev) => {
    setOpen(ev.currentTarget);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleChangeLang = (newLang) => {
    onChangeLang(newLang);
    handleClose();
  };

  return (
    <>
      {localeEnabled || isEventsLng(id) && (
        <>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0}
            sx={{
              borderRadius: '8px',
              border: (theme) => `solid 1px ${theme.palette.grey[200]}`,
              p: 0.5,
              pl: 1,
            }}
          >
            <Box
              sx={{
                width: '20px',
                height: '20px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Iconify width={20} height={20} icon={currentLang.icon} />
            </Box>
            <Typography
              variant="body2"
              component="div"
              sx={{ color: 'text.secondary', ml: 1 }}
            >
              {currentLang.label}
            </Typography>
            <Box sx={{ flexGrow: 1 }}></Box>
            <Box>
              <ExpandMore
                expand={open}
                onClick={handleOpen}
                aria-expanded={open}
                aria-label="show more"
                size="small"
              >
                <Iconify
                  width={18}
                  height={18}
                  icon={'material-symbols:keyboard-arrow-down'}
                />
              </ExpandMore>
            </Box>
          </Stack>
          <Popover
            open={open}
            onClose={handleClose}
            anchorEl={open}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            sx={{
              'mt': 1.5,
              'ml': 0.75,
              'width': 180,
              '& .MuiMenuItem-root': {
                'px': 1,
                'typography': 'body2',
                'borderRadius': '8px',
                '& svg': {
                  mr: 1,
                },
              },
              '& .MuiModal-backdrop': {
                backdropFilter: 'none',
              },
              'box-shadow': (theme) => theme.customShadows.z24,
            }}
          >
            <Stack spacing={0.75} sx={{ p: 1 }}>
              {allLangs.map((option) => (
                <MenuItem
                  key={option.value}
                  selected={option.value === currentLang.value}
                  onClick={() => handleChangeLang(option.value)}
                >
                  <Iconify width={20} height={20} icon={option.icon} />
                  {option.label}
                </MenuItem>
              ))}
            </Stack>
          </Popover>
        </>
      )}
    </>
  );
}
